import { useEffect, useState, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Plus, ChevronDown, ChevronRight, ChevronUp, Ruler } from 'lucide-react';

const INPUT_TYPES = [
  { value: 'select', label: 'Dropdown' },
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'textarea', label: 'Long text' },
  { value: 'checkbox', label: 'Checkbox' },
];

const linesToList = (s) => String(s || '').split('\n').map(x => x.trim()).filter(Boolean);
const listToLines = (l) => (Array.isArray(l) ? l : []).join('\n');

function chipStyle(tone) {
  const bg = tone === 'rule' ? 'var(--blue-bg, var(--gray-100))' : 'var(--gray-100)';
  const fg = tone === 'rule' ? 'var(--blue, var(--gray-600))' : 'var(--gray-600)';
  return {
    fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 100,
    background: bg, color: fg, whiteSpace: 'nowrap',
  };
}

// ── Price-effect rows (shared by the base editor and the customer override) ──
// ── What this question costs (v1.36.0) ───────────────────────────────────
// Set the charge where the question is written. This writes to the same
// charge_types / charge_rules the Charge Catalog manages — one mechanism, two
// ways in. The catalog stays the place to manage rates across customers.
function QuestionCharges({ attrId, code, siblings }) {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [units, setUnits] = useState([]);
  const [qtyModes, setQtyModes] = useState([]);

  const load = useCallback(() => {
    if (!attrId) { setData({ charges: [], available: [] }); return; }
    api.get(`/api/attributes/attributes/${attrId}/charges`)
      .then(r => setData(r.data))
      .catch(() => setData({ charges: [], available: [] }));
  }, [attrId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/api/charge-types/units')
      .then(r => { setUnits(r.data.units || []); setQtyModes(r.data.qty_modes || []); })
      .catch(() => {});
  }, []);

  const blank = { charge_type_id: '', label: '', unit: 'flat', default_amount: '',
                  match_value: '', qty_mode: 'fixed', qty_fixed: 1, qty_code: '', qty_times_code: '' };

  const save = async () => {
    try {
      await api.post(`/api/attributes/attributes/${attrId}/charges`, draft);
      setDraft(null); load();
      toast.success('Charge set');
    } catch (e) { toast.error(e.response?.data?.error || 'Could not set the charge'); }
  };

  const money = (v) => (v == null || v === '' ? 'no rate yet' : `$${Number(v).toFixed(2)}`);

  if (!attrId) {
    return <p className="form-hint" style={{ marginTop: 0 }}>Save the question first, then you can set what it costs.</p>;
  }
  if (!data) return <div className="text-sm text-muted">Loading…</div>;

  const existing = data.available.find(c => String(c.id) === String(draft?.charge_type_id));
  const unitOfDraft = existing ? existing.unit : draft?.unit;

  return (
    <>
      {data.charges.length === 0 && !draft && (
        <p className="form-hint" style={{ marginTop: 0 }}>
          This answer doesn't change the price.
        </p>
      )}

      {data.charges.map(c => (
        <div key={c.rule_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '3px 0', fontSize: 13 }}>
          <span style={{ flex: 1 }}>
            <strong>{c.charge_label}</strong>{' '}
            <span className="text-muted">
              {c.role === 'quantity' ? '— this answer supplies the quantity' : `— ${money(c.default_amount)}`}
              {!c.active && ' (off)'}
            </span>
          </span>
          <a className="link-btn" href="/admin/charges">rates &amp; edit</a>
        </div>
      ))}

      {draft ? (
        <div style={{ marginTop: 10, padding: 10, background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 6 }}>
          <div className="form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Charge</label>
              <select className="form-select" value={draft.charge_type_id}
                onChange={e => setDraft(d => ({ ...d, charge_type_id: e.target.value }))}>
                <option value="">Create a new charge…</option>
                {data.available.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            {!draft.charge_type_id && (
              <>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Name</label>
                  <input className="form-input" value={draft.label} placeholder="e.g. Color ink"
                    onChange={e => setDraft(d => ({ ...d, label: e.target.value }))} />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Charged</label>
                  <select className="form-select" value={draft.unit}
                    onChange={e => setDraft(d => ({ ...d, unit: e.target.value,
                      qty_mode: e.target.value === 'flat' ? 'fixed' : 'line_qty_m' }))}>
                    {units.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ flex: '0 0 130px' }}>
                  <label className="form-label">Rate ($)</label>
                  <input className="form-input" type="number" step="0.01" value={draft.default_amount}
                    placeholder="per customer" onChange={e => setDraft(d => ({ ...d, default_amount: e.target.value }))} />
                </div>
              </>
            )}
          </div>

          <div className="form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Charge when the answer is</label>
              <input className="form-input" value={draft.match_value} placeholder="any answer except No"
                onChange={e => setDraft(d => ({ ...d, match_value: e.target.value }))} />
            </div>
            {unitOfDraft && unitOfDraft !== 'flat' && (
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Quantity comes from</label>
                <select className="form-select" value={draft.qty_mode}
                  onChange={e => setDraft(d => ({ ...d, qty_mode: e.target.value }))}>
                  {qtyModes.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
            )}
            {draft.qty_mode === 'answer' && (
              <>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Answer to</label>
                  <select className="form-select" value={draft.qty_code}
                    onChange={e => setDraft(d => ({ ...d, qty_code: e.target.value }))}>
                    <option value="">Choose a question…</option>
                    {siblings.map(x => <option key={x.code} value={x.code}>{x.label}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">× (optional)</label>
                  <select className="form-select" value={draft.qty_times_code}
                    onChange={e => setDraft(d => ({ ...d, qty_times_code: e.target.value }))}>
                    <option value="">nothing</option>
                    {siblings.map(x => <option key={x.code} value={x.code}>{x.label}</option>)}
                  </select>
                </div>
              </>
            )}
            {draft.qty_mode === 'fixed' && unitOfDraft && unitOfDraft !== 'flat' && (
              <div className="form-group" style={{ flex: '0 0 110px' }}>
                <label className="form-label">How many</label>
                <input className="form-input" type="number" step="any" min="0" value={draft.qty_fixed}
                  onChange={e => setDraft(d => ({ ...d, qty_fixed: e.target.value }))} />
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn btn-primary btn-sm" onClick={save}>Save charge</button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDraft(null)}>Cancel</button>
          </div>
          <p className="form-hint" style={{ marginBottom: 0 }}>
            Per-customer rates are set in <a href="/admin/charges">Charge Catalog</a>, where you can
            also test a rule before it goes live.
          </p>
        </div>
      ) : (
        <button type="button" className="btn btn-secondary btn-sm" style={{ marginTop: 6 }}
          onClick={() => setDraft({ ...blank })}><Plus size={13} /> This answer costs money</button>
      )}
    </>
  );
}

// ── Base question editor ─────────────────────────────────────────────────
function QuestionEditor({ attr, groups, siblings, onSaved, onCancel }) {
  const isNew = !attr.id;
  const [d, setD] = useState({
    label: attr.label || '',
    code: attr.code || '',
    input_type: attr.input_type || 'select',
    group_id: attr.group_id || '',
    required: !!attr.required,
    optionsText: listToLines(Array.isArray(attr.options) ? attr.options : []),
    vwCode: attr.visible_when?.code || '',
    vwValues: (attr.visible_when?.in || []).join(', '),
    dependsOn: attr.depends_on || '',
    optionMap: (attr.depends_on && attr.options && !Array.isArray(attr.options)) ? attr.options : {},
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setD(s => ({ ...s, [k]: v }));

  const others = siblings.filter(s => s.id !== attr.id);
  const vwControl = others.find(s => s.code === d.vwCode);
  const depControl = others.find(s => s.code === d.dependsOn);
  const depKeys = Array.isArray(depControl?.options) ? depControl.options : [];
  const hasRules = !!(d.vwCode || d.dependsOn);

  const save = async () => {
    if (!d.label.trim()) return toast.error('A question label is required');
    setSaving(true);
    try {
      const payload = {
        label: d.label.trim(),
        code: d.code.trim() || d.label.trim(),
        input_type: d.input_type,
        group_id: d.group_id || null,
        required: d.required ? 1 : 0,
        depends_on: d.dependsOn || null,
        visible_when: d.vwCode
          ? { code: d.vwCode, in: d.vwValues.split(',').map(s => s.trim()).filter(Boolean) }
          : null,
        options: d.dependsOn
          ? Object.fromEntries(Object.entries(d.optionMap).map(([k, v]) => [k, Array.isArray(v) ? v : linesToList(v)]))
          : linesToList(d.optionsText),
      };
      const saved = isNew
        ? await api.post(`/api/attributes/sets/${attr.attribute_set_id}/attributes`, payload)
        : await api.patch(`/api/attributes/attributes/${attr.id}`, payload);
      const id = saved.data.attribute.id;
      toast.success(isNew ? 'Question added' : 'Question saved');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ padding: '14px 16px', background: 'var(--gray-50)', borderTop: '1px solid var(--gray-200)' }}>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Label<span className="required">*</span></label>
          <input className="form-input" value={d.label} onChange={e => set('label', e.target.value)}
            placeholder="e.g. Stock Color" />
        </div>
        <div className="form-group">
          <label className="form-label">Code</label>
          <input className="form-input" value={d.code} onChange={e => set('code', e.target.value)}
            placeholder="auto from the label" />
          <p className="form-hint">Used on orders and in rules. Letters, numbers and underscores.</p>
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Type</label>
          <select className="form-select" value={d.input_type} onChange={e => set('input_type', e.target.value)}>
            {INPUT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Group</label>
          <select className="form-select" value={d.group_id} onChange={e => set('group_id', e.target.value)}>
            <option value="">— ungrouped —</option>
            {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Required</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, height: 38, fontSize: 13 }}>
            <input type="checkbox" checked={d.required} onChange={e => set('required', e.target.checked)} />
            Must be answered
          </label>
        </div>
      </div>

      {d.input_type === 'select' && !d.dependsOn && (
        <div className="form-group">
          <label className="form-label">Options</label>
          <textarea className="form-textarea" rows={4} value={d.optionsText}
            onChange={e => set('optionsText', e.target.value)} placeholder={'Yes\nNo'} />
          <p className="form-hint">One option per line. For a Yes/No question, enter Yes and No.</p>
        </div>
      )}

      <details open={hasRules} style={{ borderTop: '1px solid var(--gray-200)', paddingTop: 12, marginTop: 4 }}>
        <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--gray-600)', marginBottom: 10 }}>
          Advanced — conditions, dependent options, pricing
        </summary>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Show only when</label>
            <select className="form-select" value={d.vwCode} onChange={e => set('vwCode', e.target.value)}>
              <option value="">— always show —</option>
              {others.map(s => <option key={s.id} value={s.code}>{s.label}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">…answers with</label>
            <input className="form-input" value={d.vwValues} disabled={!d.vwCode}
              onChange={e => set('vwValues', e.target.value)} placeholder="Yes" />
            <p className="form-hint">
              {vwControl && Array.isArray(vwControl.options) && vwControl.options.length
                ? `Comma-separated. Available: ${vwControl.options.join(', ')}`
                : 'Comma-separated list of matching answers.'}
            </p>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Options depend on</label>
          <select className="form-select" value={d.dependsOn} onChange={e => set('dependsOn', e.target.value)}>
            <option value="">— fixed options —</option>
            {others.filter(s => s.input_type === 'select').map(s => <option key={s.id} value={s.code}>{s.label}</option>)}
          </select>
          {d.dependsOn && (
            <p className="form-hint">
              Leave every list below blank to use the shared part-count size lists instead.
            </p>
          )}
        </div>

        {d.dependsOn && depKeys.length > 0 && (
          <div className="form-row">
            {depKeys.map(k => (
              <div className="form-group" key={k}>
                <label className="form-label">When {depControl.label} = {k}</label>
                <textarea className="form-textarea" rows={4}
                  value={typeof d.optionMap[k] === 'string' ? d.optionMap[k] : listToLines(d.optionMap[k])}
                  onChange={e => set('optionMap', { ...d.optionMap, [k]: e.target.value })} />
              </div>
            ))}
          </div>
        )}
        {d.dependsOn && depKeys.length === 0 && (
          <p className="form-hint" style={{ marginTop: 0 }}>
            {depControl
              ? `"${depControl.label}" has no options yet — add them there first to fill in per-answer lists.`
              : 'Pick a dropdown question above to key the option lists by.'}
          </p>
        )}

        <div className="form-group" style={{ marginTop: 10 }}>
          <label className="form-label">Pricing</label>
          <QuestionCharges attrId={attr.id} code={d.code} siblings={siblings || []} />
        </div>
      </details>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
        <button className="btn btn-secondary btn-sm" onClick={onCancel}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : (isNew ? 'Add question' : 'Save question')}
        </button>
      </div>
    </div>
  );
}

// ── Per-customer override editor ─────────────────────────────────────────
function OverrideEditor({ attr, customerId, customerName, patch, pricing, onSaved, onCancel }) {
  const [on, setOn] = useState(!!patch);
  const [hidden, setHidden] = useState(!!patch?.hidden);
  const [req, setReq] = useState(patch?.required === undefined ? '' : String(patch.required));
  const [optionsText, setOptionsText] = useState(listToLines(patch?.options));
  const [saving, setSaving] = useState(false);

  const baseOptions = Array.isArray(attr.options) ? attr.options : [];

  const save = async () => {
    setSaving(true);
    try {
      const body = { customer_id: customerId, patch: {} };
      if (on) {
        const opts = linesToList(optionsText);
        if (opts.length) body.patch.options = opts;
        if (req !== '') body.patch.required = parseInt(req, 10);
        if (hidden) body.patch.hidden = true;
      }
      await api.put(`/api/attributes/attributes/${attr.id}/override`, body);
      toast.success(`Saved for ${customerName}`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ padding: '14px 16px', background: 'var(--gray-50)', borderTop: '1px solid var(--gray-200)' }}>
      <div className="form-group">
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600 }}>
          <input type="checkbox" checked={on} onChange={e => setOn(e.target.checked)} />
          Override this question for {customerName}
        </label>
        <p className="form-hint">
          Unchecked, {customerName} sees the base question exactly as authored. Charges below are saved
          either way — they're set per customer, not on the item.
        </p>
      </div>

      {on && (
        <>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Required</label>
              <select className="form-select" value={req} onChange={e => setReq(e.target.value)}>
                <option value="">Use base ({attr.required ? 'required' : 'optional'})</option>
                <option value="1">Required</option>
                <option value="0">Optional</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Visibility</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, height: 38, fontSize: 13 }}>
                <input type="checkbox" checked={hidden} onChange={e => setHidden(e.target.checked)} />
                Hide from this customer
              </label>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Options</label>
            <textarea className="form-textarea" rows={4} value={optionsText}
              onChange={e => setOptionsText(e.target.value)}
              placeholder={baseOptions.length ? baseOptions.join('\n') : 'Leave blank to use the base options'} />
            <p className="form-hint">Leave blank to use the base options.</p>
          </div>
        </>
      )}

      <div className="form-group" style={{ marginTop: 10 }}>
        <label className="form-label">Price effect for {customerName}</label>
        <QuestionCharges attrId={attr.id} code={attr.code} siblings={[]} />
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
        <button className="btn btn-secondary btn-sm" onClick={onCancel}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save for this customer'}
        </button>
      </div>
    </div>
  );
}

// ── Shared part-count → size lists ───────────────────────────────────────
function SizeListsModal({ onClose }) {
  const [rows, setRows] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/api/attributes/size-lists')
      .then(r => setRows(r.data.lists.map(l => ({ part_count: l.part_count, text: listToLines(l.sizes) }))))
      .catch(() => setRows([]));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/api/attributes/size-lists', {
        lists: rows.map(r => ({ part_count: r.part_count, sizes: linesToList(r.text) })),
      });
      toast.success('Size lists saved');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 720 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Part-count size lists</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <p className="text-sm text-muted" style={{ marginTop: 0 }}>
            Shared across every item. A Size question whose options depend on a part-count question pulls
            from these, unless the item has its own size list override.
          </p>
          {!rows ? <div className="loading">Loading…</div> : (
            <>
              <div className="form-row">
                {rows.map((r, i) => (
                  <div className="form-group" key={r.part_count}>
                    <label className="form-label">{r.part_count}-part</label>
                    <textarea className="form-textarea" rows={6} value={r.text}
                      onChange={e => setRows(rows.map((x, j) => j === i ? { ...x, text: e.target.value } : x))} />
                  </div>
                ))}
              </div>
              <button className="btn btn-secondary btn-sm" onClick={() => {
                const next = Math.max(0, ...rows.map(r => r.part_count)) + 1;
                setRows([...rows, { part_count: next, text: '' }]);
              }}>
                <Plus size={13} /> Add part count
              </button>
              <p className="form-hint">One size per line. Clearing a list removes that part count.</p>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving || !rows}>
            {saving ? 'Saving…' : 'Save size lists'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── New set modal ────────────────────────────────────────────────────────
function NewSetModal({ onClose }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) return toast.error('A set name is required');
    setSaving(true);
    try {
      const r = await api.post('/api/attributes/sets', { name: name.trim(), description });
      toast.success('Question set created');
      onClose(r.data.set.id);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Save failed');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={() => onClose(null)}>
      <div className="modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>New question set</h3>
          <button className="btn btn-ghost btn-sm" onClick={() => onClose(null)}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Name<span className="required">*</span></label>
            <input className="form-input" value={name} onChange={e => setName(e.target.value)}
              placeholder="e.g. LAZ Parking Tickets" />
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-textarea" value={description} onChange={e => setDescription(e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={() => onClose(null)}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Creating…' : 'Create set'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────
export default function AttributeBuilderPage() {
  const [sets, setSets] = useState([]);
  const [setId, setSetId] = useState('');
  const [detail, setDetail] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [overrides, setOverrides] = useState({});
  const [pricing, setPricing] = useState({});
  const [expanded, setExpanded] = useState(null);   // attribute id, or 'new'
  const [loading, setLoading] = useState(true);
  const [showSizes, setShowSizes] = useState(false);
  const [showNewSet, setShowNewSet] = useState(false);
  const [newGroup, setNewGroup] = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/api/attributes/sets'),
      api.get('/api/customers'),
    ]).then(([s, c]) => {
      setSets(s.data.sets);
      setCustomers((c.data.customers || []).filter(x => x.active));
      if (s.data.sets.length) setSetId(String(s.data.sets[0].id));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const loadDetail = (id) => {
    if (!id) { setDetail(null); return; }
    api.get(`/api/attributes/sets/${id}`).then(r => setDetail(r.data)).catch(() => setDetail(null));
  };
  const loadOverrides = (id, cid) => {
    if (!id || !cid) { setOverrides({}); setPricing({}); return; }
    api.get(`/api/attributes/sets/${id}/overrides/${cid}`)
      .then(r => { setOverrides(r.data.overrides || {}); setPricing(r.data.pricing || {}); })
      .catch(() => { setOverrides({}); setPricing({}); });
  };

  useEffect(() => { loadDetail(setId); setExpanded(null); }, [setId]);
  useEffect(() => { loadOverrides(setId, customerId); }, [setId, customerId]);

  const refresh = () => {
    loadDetail(setId);
    loadOverrides(setId, customerId);
    api.get('/api/attributes/sets').then(r => setSets(r.data.sets)).catch(() => {});
    setExpanded(null);
  };

  const attributes = detail?.attributes || [];
  const groups = detail?.groups || [];
  const customerName = customers.find(c => String(c.id) === String(customerId))?.name || '';

  const move = async (attr, direction) => {
    try {
      await api.post(`/api/attributes/attributes/${attr.id}/move`, { direction });
      loadDetail(setId);
    } catch (e) { toast.error(e.response?.data?.error || 'Could not reorder'); }
  };

  const removeQuestion = async (attr) => {
    if (!window.confirm(`Delete the question "${attr.label}"? Answers already saved on past orders are kept.`)) return;
    try {
      await api.delete(`/api/attributes/attributes/${attr.id}`);
      toast.success('Question deleted');
      refresh();
    } catch (e) { toast.error(e.response?.data?.error || 'Delete failed'); }
  };

  const addGroup = async () => {
    if (!newGroup.trim()) return;
    try {
      await api.post(`/api/attributes/sets/${setId}/groups`, { name: newGroup.trim() });
      setNewGroup('');
      loadDetail(setId);
    } catch (e) { toast.error(e.response?.data?.error || 'Could not add group'); }
  };

  const removeGroup = async (g) => {
    if (!window.confirm(`Remove the group "${g.name}"? Its questions stay, ungrouped.`)) return;
    await api.delete(`/api/attributes/groups/${g.id}`);
    loadDetail(setId);
  };

  const chipsFor = (a) => {
    const chips = [];
    if (a.required) chips.push({ text: 'required', tone: 'plain' });
    if (a.visible_when) chips.push({ text: `shows when ${a.visible_when.code} = ${(a.visible_when.in || []).join(' / ')}`, tone: 'rule' });
    if (a.depends_on) chips.push({ text: `options from ${a.depends_on}`, tone: 'rule' });
    const drives = (a.charges || []).filter(c => c.role === 'trigger');
    if (drives.length) chips.push({ text: `charges ${drives.map(c => c.label).join(', ')}`, tone: 'rule' });
    const supplies = (a.charges || []).filter(c => c.role === 'quantity');
    if (supplies.length) chips.push({ text: `sets quantity for ${supplies.map(c => c.label).join(', ')}`, tone: 'rule' });
    return chips;
  };

  const overrideChip = (a) => {
    const p = overrides[a.id];
    const hasPricing = (pricing[a.id] || []).length > 0;
    if (!p && !hasPricing) return null;
    const bits = [];
    if (p?.hidden) bits.push('hidden');
    if (p?.options) bits.push('own options');
    if (p?.required !== undefined) bits.push(p.required ? 'required' : 'optional');
    if (hasPricing) bits.push('own pricing');
    return bits.length ? `${customerName}: ${bits.join(', ')}` : null;
  };

  return (
    <>
      <div className="topbar"><h2>Question Builder</h2></div>
      <div className="page">
        <div className="filter-bar">
          <select className="form-select" style={{ width: 260 }} value={setId} onChange={e => setSetId(e.target.value)}>
            {!sets.length && <option value="">No question sets yet</option>}
            {sets.map(s => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.question_count} question{s.question_count === 1 ? '' : 's'})
              </option>
            ))}
          </select>
          <select className="form-select" style={{ width: 240 }} value={customerId} onChange={e => setCustomerId(e.target.value)}>
            <option value="">Base question set</option>
            {customers.map(c => <option key={c.id} value={c.id}>Override for {c.name}</option>)}
          </select>
          <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setShowSizes(true)}>
            <Ruler size={13} /> Size Lists
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => setShowNewSet(true)}>
            + New Set
          </button>
        </div>

        {loading ? <div className="loading">Loading…</div> : !sets.length ? (
          <div className="card">
            <div className="empty-state">
              No question sets yet. Create one, then add the questions customers answer when they order a custom item.
            </div>
          </div>
        ) : (
          <>
            {customerId && (
              <div className="alert alert-info" style={{ marginBottom: 14 }}>
                Editing overrides for <strong>{customerName}</strong>. The base set is unchanged — switch back to
                “Base question set” to edit the questions themselves.
              </div>
            )}

            <div className="card" style={{ marginBottom: 18 }}>
              <div className="card-header"><h3>{detail?.set?.name || 'Questions'}</h3></div>
              {detail?.set?.description && <p className="text-sm text-muted" style={{ marginTop: 0 }}>{detail.set.description}</p>}
              <p className="text-sm text-muted" style={{ marginTop: 0 }}>
                {detail?.products?.length
                  ? `Used by: ${detail.products.map(p => p.name).join(', ')}`
                  : 'Not attached to any item yet — pick this set on an item in Item Manager.'}
              </p>
              {!customerId && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                  <span className="text-sm text-muted">Groups:</span>
                  {groups.length === 0 && <span className="text-sm text-muted">none</span>}
                  {groups.map(g => (
                    <span key={g.id} style={chipStyle('plain')}>
                      {g.name}
                      <button className="link-btn" style={{ marginLeft: 6 }} onClick={() => removeGroup(g)}>✕</button>
                    </span>
                  ))}
                  <input className="form-input" style={{ width: 160 }} value={newGroup} placeholder="Add a group…"
                    onChange={e => setNewGroup(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') addGroup(); }} />
                </div>
              )}
            </div>

            <div className="card" style={{ padding: 0 }}>
              {attributes.length === 0 && expanded !== 'new' && (
                <div className="empty-state">No questions in this set yet.</div>
              )}
              {attributes.map((a, i) => {
                const isOpen = expanded === a.id;
                const oc = customerId ? overrideChip(a) : null;
                return (
                  <div key={a.id} style={{ borderBottom: '1px solid var(--gray-200)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', cursor: 'pointer' }}
                      onClick={() => setExpanded(isOpen ? null : a.id)}>
                      {isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                      <strong style={{ fontSize: 14 }}>{a.label}</strong>
                      <span style={{ ...chipStyle('plain'), fontFamily: 'monospace' }}>{a.code}</span>
                      <span className="text-sm text-muted">
                        {INPUT_TYPES.find(t => t.value === a.input_type)?.label || a.input_type}
                      </span>
                      {chipsFor(a).map(c => <span key={c.text} style={chipStyle(c.tone)}>{c.text}</span>)}
                      {oc && <span style={chipStyle('rule')}>{oc}</span>}
                      <div className="actions-cell" style={{ marginLeft: 'auto' }} onClick={e => e.stopPropagation()}>
                        {!customerId && (
                          <>
                            <button className="btn btn-ghost btn-sm" title="Move up" disabled={i === 0}
                              onClick={() => move(a, 'up')}><ChevronUp size={14} /></button>
                            <button className="btn btn-ghost btn-sm" title="Move down" disabled={i === attributes.length - 1}
                              onClick={() => move(a, 'down')}><ChevronDown size={14} /></button>
                            <button className="btn btn-ghost btn-sm" style={{ color: 'var(--brand-red)' }}
                              onClick={() => removeQuestion(a)}>Delete</button>
                          </>
                        )}
                      </div>
                    </div>
                    {isOpen && (customerId ? (
                      <OverrideEditor attr={a} customerId={customerId} customerName={customerName}
                        patch={overrides[a.id]} pricing={pricing[a.id]}
                        onSaved={refresh} onCancel={() => setExpanded(null)} />
                    ) : (
                      <QuestionEditor attr={a} groups={groups} siblings={attributes}
                        onSaved={refresh} onCancel={() => setExpanded(null)} />
                    ))}
                  </div>
                );
              })}

              {!customerId && (expanded === 'new' ? (
                <QuestionEditor
                  attr={{ attribute_set_id: setId, input_type: 'select', options: [], }}
                  groups={groups} siblings={attributes}
                  onSaved={refresh} onCancel={() => setExpanded(null)} />
              ) : (
                <div style={{ padding: 14 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => setExpanded('new')}>
                    <Plus size={13} /> Add question
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {showSizes && <SizeListsModal onClose={() => setShowSizes(false)} />}
        {showNewSet && <NewSetModal onClose={(id) => {
          setShowNewSet(false);
          if (id) {
            api.get('/api/attributes/sets').then(r => { setSets(r.data.sets); setSetId(String(id)); });
          }
        }} />}
      </div>
    </>
  );
}
